import * as THREE from 'three';
import { getCanvasResolution } from '../FikoCanvasResolution';

/**
 * ULTRA PERFORMANS: Predictive Raycast System
 * Mouse hareketini tahmin edip önceden raycast yap
 *
 * Features:
 * - Kalman filter for smooth prediction
 * - Multi-frame lookahead
 * - Velocity-based prefetching
 * - Spatial locality caching
 * - Confidence-weighted results
 *
 * Benefits:
 * - Near-zero perceived latency
 * - Smoother hover feedback
 * - Better cache hit rate
 * - Reduced visual jitter
 */

interface MouseState {
  position: THREE.Vector2;
  velocity: THREE.Vector2;
  acceleration: THREE.Vector2;
  timestamp: number;
}

interface PredictionResult {
  position: THREE.Vector2;
  confidence: number;
  framesAhead: number;
}

interface CachedRaycast {
  screenPos: THREE.Vector2;
  result: THREE.Intersection | null;
  timestamp: number;
  hitCount: number;
}

// Pre-allocated buffers
const _tempVec2A = new THREE.Vector2();
const _tempVec2B = new THREE.Vector2();
const _tempVec3 = new THREE.Vector3();

export class FikoPredictiveRaycast {
  private camera: THREE.Camera;
  private scene: THREE.Scene;
  private raycaster: THREE.Raycaster;

  // Mouse state tracking
  private mouseStates: MouseState[] = [];
  private maxStates = 10;

  // Kalman filter state
  private kalman = {
    x: new THREE.Vector2(),      // Position estimate
    v: new THREE.Vector2(),      // Velocity estimate
    P: 1,                        // Estimate covariance
    Q: 0.1,                      // Process noise
    R: 0.5                       // Measurement noise
  };

  // Prediction cache
  private predictionCache = new Map<string, CachedRaycast>();
  private cacheGridSize = 20; // pixels
  private maxCacheSize = 200;

  // Configuration
  private config = {
    enabled: true,
    predictionFrames: 3,
    minConfidence: 0.3,
    maxPredictionDistance: 100, // pixels
    cacheEnabled: true,
    cacheTTL: 500, // ms
    prefetchRadius: 2 // grid cells
  };

  // Stats
  private stats = {
    predictions: 0,
    cacheHits: 0,
    cacheMisses: 0,
    averageConfidence: 0
  };

  // Current predictions
  private predictions: PredictionResult[] = [];

  constructor(camera: THREE.Camera, scene: THREE.Scene) {
    this.camera = camera;
    this.scene = scene;
    this.raycaster = new THREE.Raycaster();
    this.raycaster.firstHitOnly = true;
  }

  /**
   * Update with new mouse position
   */
  updateMousePosition(screenX: number, screenY: number): void {
    const now = performance.now();
    const pos = new THREE.Vector2(screenX, screenY);

    // Calculate velocity and acceleration
    let velocity = new THREE.Vector2();
    let acceleration = new THREE.Vector2();

    if (this.mouseStates.length > 0) {
      const last = this.mouseStates[this.mouseStates.length - 1];
      const dt = (now - last.timestamp) / 1000;

      if (dt > 0) {
        velocity.copy(pos).sub(last.position).divideScalar(dt);

        if (this.mouseStates.length > 1) {
          acceleration.copy(velocity).sub(last.velocity).divideScalar(dt);
        }
      }
    }

    // Add new state
    this.mouseStates.push({
      position: pos,
      velocity,
      acceleration,
      timestamp: now
    });

    // Keep only recent states
    while (this.mouseStates.length > this.maxStates) {
      this.mouseStates.shift();
    }

    // Update Kalman filter
    this.updateKalmanFilter(pos, velocity);

    // Generate predictions
    this.generatePredictions();

    // Prefetch predicted positions
    if (this.config.cacheEnabled && this.config.enabled) {
      this.prefetchPredictions();
    }
  }

  /**
   * Update Kalman filter with new measurement
   */
  private updateKalmanFilter(pos: THREE.Vector2, velocity: THREE.Vector2): void {
    // Prediction step
    this.kalman.x.add(_tempVec2A.copy(this.kalman.v).multiplyScalar(0.016)); // ~60fps
    this.kalman.P += this.kalman.Q;

    // Update step
    const K = this.kalman.P / (this.kalman.P + this.kalman.R);
    _tempVec2A.copy(pos).sub(this.kalman.x);
    this.kalman.x.add(_tempVec2A.multiplyScalar(K));

    _tempVec2A.copy(velocity).sub(this.kalman.v);
    this.kalman.v.add(_tempVec2A.multiplyScalar(K * 0.5));

    this.kalman.P *= (1 - K);
  }

  /**
   * Generate predictions for multiple frames ahead
   */
  private generatePredictions(): void {
    this.predictions = [];

    if (!this.config.enabled || this.mouseStates.length < 2) return;

    const frameTime = 16.67; // ms

    for (let i = 1; i <= this.config.predictionFrames; i++) {
      const prediction = this.predictPosition(i * frameTime);
      this.predictions.push(prediction);
    }

    // Update stats
    const avgConf = this.predictions.reduce((sum, p) => sum + p.confidence, 0) /
      Math.max(1, this.predictions.length);
    this.stats.averageConfidence = this.stats.averageConfidence * 0.9 + avgConf * 0.1;
  }

  /**
   * Predict position at future time
   */
  private predictPosition(futureMs: number): PredictionResult {
    const dt = futureMs / 1000;

    // Use Kalman filtered state for prediction
    const predictedPos = new THREE.Vector2();
    predictedPos.copy(this.kalman.x);
    predictedPos.add(_tempVec2A.copy(this.kalman.v).multiplyScalar(dt));

    // Calculate confidence based on:
    // - Velocity stability
    // - Prediction distance
    // - Historical accuracy

    const velocityMag = this.kalman.v.length();
    const predictionDist = _tempVec2A.copy(this.kalman.v).multiplyScalar(dt).length();

    // Confidence decreases with prediction distance
    let confidence = 1.0;
    confidence *= Math.exp(-predictionDist / this.config.maxPredictionDistance);

    // Confidence based on velocity consistency
    if (this.mouseStates.length >= 3) {
      const velocityVariance = this.calculateVelocityVariance();
      confidence *= Math.exp(-velocityVariance * 0.01);
    }

    // Higher velocity = more confident in direction
    confidence *= Math.min(1, velocityMag / 500);

    this.stats.predictions++;

    return {
      position: predictedPos,
      confidence: Math.max(0, Math.min(1, confidence)),
      framesAhead: Math.round(futureMs / 16.67)
    };
  }

  /**
   * Calculate velocity variance for confidence
   */
  private calculateVelocityVariance(): number {
    if (this.mouseStates.length < 3) return 0;

    const velocities = this.mouseStates.map(s => s.velocity);
    const avgVel = new THREE.Vector2();

    velocities.forEach(v => avgVel.add(v));
    avgVel.divideScalar(velocities.length);

    let variance = 0;
    velocities.forEach(v => {
      variance += v.distanceToSquared(avgVel);
    });

    return variance / velocities.length;
  }

  /**
   * Prefetch raycasts for predicted positions
   */
  private prefetchPredictions(): void {
    const now = performance.now();

    this.predictions.forEach(pred => {
      if (pred.confidence < this.config.minConfidence) return;

      const cacheKey = this.getCacheKey(pred.position);

      // Skip if already cached and fresh
      const cached = this.predictionCache.get(cacheKey);
      if (cached && now - cached.timestamp < this.config.cacheTTL) {
        return;
      }

      // Perform raycast and cache
      const result = this.performRaycast(pred.position);
      this.cacheResult(pred.position, result);
    });

    // Also prefetch nearby grid cells
    if (this.predictions.length > 0) {
      this.prefetchNearby(this.predictions[0].position);
    }
  }

  /**
   * Prefetch nearby grid cells
   */
  private prefetchNearby(center: THREE.Vector2): void {
    const now = performance.now();
    const gridX = Math.floor(center.x / this.cacheGridSize);
    const gridY = Math.floor(center.y / this.cacheGridSize);

    for (let dx = -this.config.prefetchRadius; dx <= this.config.prefetchRadius; dx++) {
      for (let dy = -this.config.prefetchRadius; dy <= this.config.prefetchRadius; dy++) {
        const pos = new THREE.Vector2(
          (gridX + dx + 0.5) * this.cacheGridSize,
          (gridY + dy + 0.5) * this.cacheGridSize
        );

        const key = this.getCacheKey(pos);
        const cached = this.predictionCache.get(key);

        if (!cached || now - cached.timestamp > this.config.cacheTTL) {
          // Low priority prefetch - only if we have budget
          if (this.predictionCache.size < this.maxCacheSize * 0.8) {
            const result = this.performRaycast(pos);
            this.cacheResult(pos, result);
          }
        }
      }
    }
  }

  /**
   * Get raycast result (from cache or computed)
   */
  getRaycastResult(screenX: number, screenY: number): THREE.Intersection | null {
    const pos = new THREE.Vector2(screenX, screenY);
    const cacheKey = this.getCacheKey(pos);

    // Check cache
    const cached = this.predictionCache.get(cacheKey);
    if (cached && performance.now() - cached.timestamp < this.config.cacheTTL) {
      cached.hitCount++;
      this.stats.cacheHits++;
      return cached.result;
    }

    // Cache miss - compute and cache
    this.stats.cacheMisses++;
    const result = this.performRaycast(pos);
    this.cacheResult(pos, result);

    return result;
  }

  /**
   * Perform actual raycast
   */
  private performRaycast(screenPos: THREE.Vector2): THREE.Intersection | null {
    const _res = getCanvasResolution();
    _tempVec2A.set(
      (screenPos.x / _res.width) * 2 - 1,
      -(screenPos.y / _res.height) * 2 + 1
    );

    this.raycaster.setFromCamera(_tempVec2A, this.camera);

    const intersections = this.raycaster.intersectObjects(this.scene.children, true);
    return intersections.length > 0 ? intersections[0] : null;
  }

  /**
   * Cache raycast result
   */
  private cacheResult(pos: THREE.Vector2, result: THREE.Intersection | null): void {
    const key = this.getCacheKey(pos);

    this.predictionCache.set(key, {
      screenPos: pos.clone(),
      result,
      timestamp: performance.now(),
      hitCount: 0
    });

    // Evict old entries
    if (this.predictionCache.size > this.maxCacheSize) {
      this.evictOldEntries();
    }
  }

  /**
   * Get cache key for position
   */
  private getCacheKey(pos: THREE.Vector2): string {
    const gx = Math.floor(pos.x / this.cacheGridSize);
    const gy = Math.floor(pos.y / this.cacheGridSize);
    return `${gx},${gy}`;
  }

  /**
   * Evict old cache entries
   */
  private evictOldEntries(): void {
    const now = performance.now();
    const toDelete: string[] = [];

    this.predictionCache.forEach((entry, key) => {
      if (now - entry.timestamp > this.config.cacheTTL) {
        toDelete.push(key);
      }
    });

    // If still too many, remove least used
    if (this.predictionCache.size - toDelete.length > this.maxCacheSize) {
      const entries = Array.from(this.predictionCache.entries())
        .filter(([k]) => !toDelete.includes(k))
        .sort((a, b) => a[1].hitCount - b[1].hitCount);

      const removeCount = entries.length - this.maxCacheSize;
      for (let i = 0; i < removeCount; i++) {
        toDelete.push(entries[i][0]);
      }
    }

    toDelete.forEach(k => this.predictionCache.delete(k));
  }

  /**
   * Get current predictions
   */
  getPredictions(): PredictionResult[] {
    return this.predictions;
  }

  /**
   * Get best prediction (highest confidence)
   */
  getBestPrediction(): PredictionResult | null {
    if (this.predictions.length === 0) return null;

    return this.predictions.reduce((best, pred) =>
      pred.confidence > best.confidence ? pred : best
    );
  }

  /**
   * Get mouse velocity
   */
  getMouseVelocity(): THREE.Vector2 {
    return this.kalman.v.clone();
  }

  /**
   * Get statistics
   */
  getStats(): {
    predictions: number;
    cacheHits: number;
    cacheMisses: number;
    hitRate: string;
    averageConfidence: string;
    cacheSize: number;
    velocity: string;
  } {
    const total = this.stats.cacheHits + this.stats.cacheMisses;
    const hitRate = total > 0 ? (this.stats.cacheHits / total * 100).toFixed(1) : '0';

    return {
      predictions: this.stats.predictions,
      cacheHits: this.stats.cacheHits,
      cacheMisses: this.stats.cacheMisses,
      hitRate: `${hitRate}%`,
      averageConfidence: `${(this.stats.averageConfidence * 100).toFixed(1)}%`,
      cacheSize: this.predictionCache.size,
      velocity: `${this.kalman.v.length().toFixed(1)}px/s`
    };
  }

  /**
   * Configure system
   */
  configure(options: Partial<typeof this.config>): void {
    Object.assign(this.config, options);
  }

  /**
   * Clear cache
   */
  clearCache(): void {
    this.predictionCache.clear();
  }

  /**
   * Reset state
   */
  reset(): void {
    this.mouseStates = [];
    this.predictions = [];
    this.kalman.x.set(0, 0);
    this.kalman.v.set(0, 0);
    this.kalman.P = 1;
    this.clearCache();
  }

  /**
   * Cleanup
   */
  dispose(): void {
    this.reset();
  }
}

/**
 * ULTRA PERFORMANS: Spatial Raycast Accelerator
 * BVH + Screen-space grid hybrid
 */
export class FikoSpatialRaycastAccelerator {
  private scene: THREE.Scene;
  private camera: THREE.Camera;

  // Screen-space grid for fast lookups
  private screenGrid: Map<string, THREE.Object3D[]> = new Map();
  private gridCellSize = 50; // pixels

  // Object bounds cache
  private boundsCache = new Map<string, THREE.Box3>();

  // Configuration
  private gridWidth = 0;
  private gridHeight = 0;

  constructor(scene: THREE.Scene, camera: THREE.Camera) {
    this.scene = scene;
    this.camera = camera;
  }

  /**
   * Rebuild screen-space grid
   */
  rebuild(width: number, height: number): void {
    this.gridWidth = Math.ceil(width / this.gridCellSize);
    this.gridHeight = Math.ceil(height / this.gridCellSize);

    this.screenGrid.clear();

    // Project all objects and bin them
    this.scene.traverse(obj => {
      if (obj instanceof THREE.Mesh) {
        const screenBounds = this.getScreenBounds(obj);
        if (!screenBounds) return;

        // Get grid cells this object overlaps
        const minX = Math.floor(screenBounds.min.x / this.gridCellSize);
        const maxX = Math.ceil(screenBounds.max.x / this.gridCellSize);
        const minY = Math.floor(screenBounds.min.y / this.gridCellSize);
        const maxY = Math.ceil(screenBounds.max.y / this.gridCellSize);

        for (let x = minX; x <= maxX; x++) {
          for (let y = minY; y <= maxY; y++) {
            const key = `${x},${y}`;
            if (!this.screenGrid.has(key)) {
              this.screenGrid.set(key, []);
            }
            this.screenGrid.get(key)!.push(obj);
          }
        }
      }
    });
  }

  /**
   * Get screen bounds for object
   */
  private getScreenBounds(obj: THREE.Mesh): { min: THREE.Vector2; max: THREE.Vector2 } | null {
    if (!obj.geometry.boundingBox) {
      obj.geometry.computeBoundingBox();
    }

    const box = obj.geometry.boundingBox!;
    const corners = [
      new THREE.Vector3(box.min.x, box.min.y, box.min.z),
      new THREE.Vector3(box.max.x, box.min.y, box.min.z),
      new THREE.Vector3(box.min.x, box.max.y, box.min.z),
      new THREE.Vector3(box.max.x, box.max.y, box.min.z),
      new THREE.Vector3(box.min.x, box.min.y, box.max.z),
      new THREE.Vector3(box.max.x, box.min.y, box.max.z),
      new THREE.Vector3(box.min.x, box.max.y, box.max.z),
      new THREE.Vector3(box.max.x, box.max.y, box.max.z)
    ];

    const screenMin = new THREE.Vector2(Infinity, Infinity);
    const screenMax = new THREE.Vector2(-Infinity, -Infinity);

    let allBehind = true;

    corners.forEach(corner => {
      corner.applyMatrix4(obj.matrixWorld);
      corner.project(this.camera);

      if (corner.z < 1) {
        allBehind = false;
        const _cRes = getCanvasResolution();
        const sx = (corner.x + 1) * 0.5 * _cRes.width;
        const sy = (1 - corner.y) * 0.5 * _cRes.height;

        screenMin.x = Math.min(screenMin.x, sx);
        screenMin.y = Math.min(screenMin.y, sy);
        screenMax.x = Math.max(screenMax.x, sx);
        screenMax.y = Math.max(screenMax.y, sy);
      }
    });

    if (allBehind) return null;

    return { min: screenMin, max: screenMax };
  }

  /**
   * Get candidate objects at screen position
   */
  getCandidates(screenX: number, screenY: number): THREE.Object3D[] {
    const gx = Math.floor(screenX / this.gridCellSize);
    const gy = Math.floor(screenY / this.gridCellSize);
    const key = `${gx},${gy}`;

    return this.screenGrid.get(key) || [];
  }

  /**
   * Get grid statistics
   */
  getStats(): {
    totalCells: number;
    occupiedCells: number;
    averageObjectsPerCell: number;
  } {
    const occupiedCells = this.screenGrid.size;
    let totalObjects = 0;

    this.screenGrid.forEach(objs => {
      totalObjects += objs.length;
    });

    return {
      totalCells: this.gridWidth * this.gridHeight,
      occupiedCells,
      averageObjectsPerCell: occupiedCells > 0 ? totalObjects / occupiedCells : 0
    };
  }

  /**
   * Cleanup
   */
  dispose(): void {
    this.screenGrid.clear();
    this.boundsCache.clear();
  }
}
